import { Injectable } from '@angular/core';
import { IPowerIdeaPadVantageShell } from 'src/app/data-models/power/power-ideapad-vantage-shell.interface';
import { IPowerThinkPadVantageShell } from 'src/app/data-models/power/power-thinkpad.vantage-shell.interface';
import { EMachineType } from 'src/app/enums/machine-type.enum';
import { DeviceService } from '../../device/device.service';
import { LoggerService } from '../../logger/logger.service';
import { VantageShellService } from '../../vantage-shell/vantage-shell.service';
import { FlipToBootErrorCodeEnum, FlipToBootSupportedEnum } from '../flipToBoot.enum';
import { FlipToBootErrorStatusInterface, FlipToBootInterface, FlipToBootSetStatus } from '../flipToBoot.interface';
import { PowerSettingsIdeapad } from './ideapad/power-settings-ideapad';
import { PowerSettingsBase } from './power-settings.base';
import { PowerSettingsThinkpad } from './thinkpad/power-settings-thinkpad';

@Injectable({
	providedIn: 'root'
})
export class PowerSettingsService {

	private powerSettings: PowerSettingsBase;

	constructor(
		private deviceService: DeviceService,
		private vantageShellService: VantageShellService,
		private logger: LoggerService) { }

	public async setAlwaysOnUSBStatus(isEnabled: boolean): Promise<boolean> {
		await this.checkInitialization();

		return this.powerSettings.setAlwaysOnUSBStatus(isEnabled);
	}

	public async getAlwaysOnUSBCapability(): Promise<boolean> {
		await this.checkInitialization();

		return this.powerSettings.getAlwaysOnUSBCapability();
	}

	public async setUSBCharging(isEnabled: boolean) {
		await this.checkInitialization();

		return this.powerSettings.setUSBCharging(isEnabled);
	}

	public async getUSBChargingCapability(): Promise<boolean> {
		await this.checkInitialization();

		return this.powerSettings.getUSBChargingCapability();
	}

	public async getAirplaneModeCapability(): Promise<boolean> {
		await this.checkInitialization();

		return this.powerSettings.getAirplaneModeCapability();
	}

	public async setAirplaneMode(isEnabled: boolean): Promise<boolean> {
		await this.checkInitialization();

		return this.powerSettings.setAirplaneMode(isEnabled);
	}

	public async setAirplaneModeAutoDetection(isEnabled: boolean): Promise<boolean> {
		await this.checkInitialization();

		return this.powerSettings.setAirplaneModeAutoDetection(isEnabled);
	}

	public async setEasyResume(isEnabled: boolean): Promise<boolean> {
		await this.checkInitialization();

		return this.powerSettings.setEasyResume(isEnabled);
	}

	public async getEasyResumeCapability(): Promise<boolean> {
		await this.checkInitialization();

		return this.powerSettings.getEasyResumeCapability();
	}

	public async setFlipToBoot(status: FlipToBootSetStatus): Promise<FlipToBootErrorStatusInterface> {
		await this.checkInitialization();

		return this.powerSettings.setFlipToBoot(status)
	}

	public async getFlipToBootCapability(): Promise<boolean> {
		await this.checkInitialization();

		const capability: FlipToBootInterface = await this.powerSettings.getFlipToBoot();
		const hasCapability = +capability.ErrorCode === FlipToBootErrorCodeEnum.Succeed && +capability.Supported === FlipToBootSupportedEnum.Succeed;

		return hasCapability;
	}

	private async checkInitialization() {
		if (!this.powerSettings) {
			await this.initialize();
		}
	}

	private async initialize() {
		try {
			const machineType: EMachineType = await this.deviceService.getMachineType();
			switch (machineType) {
				case EMachineType.ThinkPad:
					const powerThinkPadVantageShell: IPowerThinkPadVantageShell = this.vantageShellService.getPowerThinkPad();
					this.powerSettings = new PowerSettingsThinkpad(powerThinkPadVantageShell);
					break;
				case EMachineType.IdeaPad:
					const powerIdeapadVantageShell: IPowerIdeaPadVantageShell = this.vantageShellService.getPowerIdeaNoteBook();
					this.powerSettings = new PowerSettingsIdeapad(powerIdeapadVantageShell);
					break;
				default:
					throw new Error(`Power settings not implemented for: ${machineType}`);
			}
		} catch (error) {
			this.logger.error('PowerSettingsService - initialize - error', error);
		}
	}
}
